import { Immutable } from "immer";
import React from "react";
import { slugify } from "tsi-common-react/src/utils/text";
import { t } from "ttag";

import type { TempurFinanceFAQsBlock as Value } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";

type Props = Immutable<{
    value: Value;
}>;

/**
 * Faithful port of the legacy server-rendered `finance-faq.html`. Each FAQ owns
 * its accordion open/close state (the AccordionBlock precedent): a click on the
 * `.accordion` question toggles the `.accordion` / `.accordion-target` active
 * classes plus `aria-expanded` / `aria-hidden`. The "Show All FAQs..." reveal is
 * owned by the parent via local `revealed` state: clicking `.finance-faq__see-more`
 * un-hides every `.finance-faq--hidden` article and hides the button.
 *
 * The legacy template was a bare `{% for %}` with no wrapping element, so we
 * return a fragment (matching the server output exactly). `forloop.counter` is
 * 1-indexed; items 6+ (counter > 5) get `finance-faq--hidden`, and the
 * see-more button is emitted once, right after the article whose counter == 6.
 */
const FinanceFAQItem = (
    props: Immutable<{
        question: string;
        answer: string;
        targetID: string;
        hidden: boolean;
    }>,
): React.JSX.Element => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    return (
        <article
            className={
                props.hidden ? "finance-faq finance-faq--hidden" : "finance-faq"
            }
        >
            <h4>
                <button
                    className={
                        isExpanded
                            ? "finance-faq__question accordion accordion--is-active"
                            : "finance-faq__question accordion"
                    }
                    aria-controls={props.targetID}
                    aria-expanded={isExpanded ? "true" : "false"}
                    onClick={(event) => {
                        event.preventDefault();
                        setIsExpanded((value) => !value);
                    }}
                    dangerouslySetInnerHTML={{
                        __html: props.question,
                    }}
                />
            </h4>
            <EditorContent>
                {(marker) => (
                    <div
                        className={
                            isExpanded
                                ? "finance-faq__answer accordion-target accordion-target--is-active"
                                : "finance-faq__answer accordion-target"
                        }
                        id={props.targetID}
                        aria-hidden={isExpanded ? "false" : "true"}
                        dangerouslySetInnerHTML={{ __html: props.answer }}
                        {...marker}
                    />
                )}
            </EditorContent>
        </article>
    );
};

export const TempurFinanceFAQsBlock = (props: Props): React.JSX.Element => {
    const faqs = props.value.faqs ?? [];
    const [revealed, setRevealed] = React.useState(false);
    return (
        <>
            {faqs.map((item, idx) => {
                const counter = idx + 1;
                const question = item.question ?? "";
                const answer = item.answer ?? "";
                // Reproduce Django `slugify(block.question)`. The question may
                // contain HTML, so strip tags first (Django renders it raw via
                // `|safe`, but slugify operates on the model value); the result
                // is not pixel-visible — it only needs to keep the
                // `aria-controls`/`id` pair linked. Compute once and reuse for
                // both so the accordion association holds.
                const slug = slugify(question.replace(/<[^>]*>/g, " "));
                const targetID = `faq-${slug}-${counter}`;
                return (
                    // Index-derived key: ListBlock items surface no stable id, so
                    // a CMS reorder reuses positional DOM — matches the legacy
                    // `forloop`-positional render.
                    <React.Fragment key={idx}>
                        <FinanceFAQItem
                            question={question}
                            answer={answer}
                            targetID={targetID}
                            hidden={counter > 5 && !revealed}
                        />
                        {counter === 6 && (
                            <button
                                className={
                                    revealed
                                        ? "finance-faq__see-more al-financing-page__show-all-faqs finance-faq__see-more--hidden"
                                        : "finance-faq__see-more al-financing-page__show-all-faqs"
                                }
                                onClick={() => {
                                    setRevealed(true);
                                }}
                            >
                                {t`Show All FAQs...`}
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};
